import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { IoExit, IoGitNetwork, IoHelpCircle, IoInformationCircle, IoPerson, IoSettings } from "react-icons/io5";
import ApiConfig from "../../../api/config";
import { Keycloaktokens, User } from "../../../storage/store";
import { forgetPushToken } from "../../../firebase/push";
import { APP_SETTINGS_PATHS } from "../../../pages/Settings/paths";
import { ADMIN_PATHS, isAdmin } from "../../../pages/Admin/paths";

// Links the app's side menu opens (app/services/CommonService.js).
const PROFILE_URL = `${ApiConfig.authUrl}realms/HASHh/account`;
const SHOP_URL = "https://www.hashh.io/products";

// Ends the login server's session too, or Login would sign the same account
// straight back in (its cookies belong to the login server, so the dashboard
// can't clear them). The login server then returns to the dashboard's Login.
const endLoginSession = (idToken) => {
  const url = new URL(`${ApiConfig.authUrl}realms/HASHh/protocol/openid-connect/logout`);
  url.searchParams.set("post_logout_redirect_uri", `${window.location.origin}/Login`);
  url.searchParams.set("client_id", "hashh-dashboard");
  if (idToken) url.searchParams.set("id_token_hint", idToken);
  window.location.assign(url.toString());
};

export const logOut = async () => {
  // Like the app, stop pushes to this browser before signing out; don't let a
  // slow server hold up the logout.
  await Promise.race([forgetPushToken().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 3000))]);
  const idToken = Keycloaktokens.getIdToken();
  Keycloaktokens.removeToken();
  User.removeUser();
  User.removePropertyId();
  endLoginSession(idToken);
};

// Full-screen menu that slides in from the left (app/components/SideMenu.js).
const SideMenu = ({ open, onClose }) => {
  const history = useHistory();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const go = (path) => {
    onClose();
    history.push(path);
  };

  const external = (href) => ({ href, target: "_blank", rel: "noopener noreferrer", onClick: onClose });
  // The admin account also gets its Automation page, after Extras.
  const admin = isAdmin();
  const after = admin ? 1 : 0;

  return (
    <nav
      id="hx-side-menu"
      className={`hx-menu ${open ? "is-open" : ""}`}
      aria-label="Menu"
      aria-hidden={!open}
    >
      <ul className="hx-menu-list">
        <li>
          <a className="hx-menu-item" style={{ "--i": 0 }} tabIndex={open ? 0 : -1} {...external(PROFILE_URL)}>
            <IoPerson />
            Profile
          </a>
        </li>
        <li>
          <a className="hx-menu-item" style={{ "--i": 1 }} tabIndex={open ? 0 : -1} {...external(SHOP_URL)}>
            <AiOutlineShoppingCart />
            Buy Products
          </a>
        </li>
        <li>
          <button
            type="button"
            className="hx-menu-item"
            style={{ "--i": 2 }}
            tabIndex={open ? 0 : -1}
            onClick={() => go(APP_SETTINGS_PATHS.HOME)}
          >
            <IoSettings />
            Settings
          </button>
        </li>
        <li>
          <button type="button" className="hx-menu-item" style={{ "--i": 3 }} tabIndex={open ? 0 : -1} onClick={() => go("/extras")}>
            <IoInformationCircle />
            Extras
          </button>
        </li>
        {admin ? (
          <li>
            <button
              type="button"
              className="hx-menu-item"
              style={{ "--i": 4 }}
              tabIndex={open ? 0 : -1}
              onClick={() => go(ADMIN_PATHS.AUTOMATION)}
            >
              <IoGitNetwork />
              Automation (admin)
            </button>
          </li>
        ) : null}
        <li>
          <button
            type="button"
            className="hx-menu-item"
            style={{ "--i": 4 + after }}
            tabIndex={open ? 0 : -1}
            onClick={() => go("/help-center")}
          >
            <IoHelpCircle />
            Help Center
          </button>
        </li>
        <li>
          <button
            type="button"
            className="hx-menu-item is-danger"
            style={{ "--i": 5 + after }}
            tabIndex={open ? 0 : -1}
            onClick={logOut}
          >
            <IoExit />
            Logout
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default SideMenu;
